import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  LoginRequestBodyType,
  LoginResponseBodyUserType,
} from "@intelligems/ig-types";
import { useGetOrgList } from "@src/api/organizations-list/get-organization-list";
import { shopifyGqlClient } from "@src/providers/gqlProvider";
import { GENERAL_ROUTES } from "@src/routes/routes";
import {
  clearAllExperienceTableFilters,
  clearSnoozeShopifyScopesDialog,
} from "@src/store/slices/appSettings";
import { resetCartTargeting } from "@src/store/slices/cartTargeting";
import { clearDashboardFilters } from "@src/store/slices/dashboardFilters";
import { resetExperience } from "@src/store/slices/experiences";
import { clearGlobal } from "@src/store/slices/global";
import { resetRedirects } from "@src/store/slices/redirects";
import { clearSitewideDashboardFilters } from "@src/store/slices/sitewideDashboardFilters";
import { resetUpsellProductRules } from "@src/store/slices/upsellProductRules";
import { resetUserInterfaces } from "@src/store/slices/userInterfaces";
import { resetWidgets } from "@src/store/slices/widgets";
import { IgStorage } from "@src/utils/igStorage";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";
import { organizationsKeys, orgListKeys, userKeys } from "../queryKeys";

export const switchOrganization = async (
  token: string,
  orgId?: string,
  shopId?: string
): Promise<LoginResponseBodyUserType> => {
  try {
    const resp = await api.post<
      LoginResponseBodyUserType,
      LoginRequestBodyType
    >("/v2/login", {
      token,
      orgId,
      shopId,
    });
    return resp.data;
  } catch (error) {
    error["suppressToast"] = true;
    throw error;
  }
};

export const useSwitchOrganization = (targetRoute?: string) => {
  const queryClient = useQueryClient();
  const orgs = useGetOrgList();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  return useMutation<LoginResponseBodyUserType, Error, any, any>({
    mutationKey: [{ scope: "user" }],
    mutationFn: async (body: {
      token: string;
      orgId?: string;
      shopId?: string;
    }) => switchOrganization(body.token, body.orgId, body.shopId),
    onSuccess: async (data) => {
      dispatch(clearGlobal());
      await shopifyGqlClient.resetStore();

      // eslint-disable-next-line react-compiler/react-compiler
      IgStorage.authToken = data.token;
      dispatch(clearDashboardFilters());
      dispatch(clearAllExperienceTableFilters());
      dispatch(clearSitewideDashboardFilters());
      dispatch(resetExperience());
      dispatch(resetRedirects());
      dispatch(resetUserInterfaces());
      dispatch(resetWidgets());
      dispatch(resetCartTargeting());
      dispatch(resetUpsellProductRules());
      dispatch(clearSnoozeShopifyScopesDialog());
      const orgsList = structuredClone(orgs.data?.orgs || []);
      queryClient.clear();
      queryClient.setQueryData(userKeys.all, data);
      queryClient.setQueryData(organizationsKeys.one, {
        org: data.organization,
      });
      queryClient.setQueryData(orgListKeys.all, { orgs: orgsList });
      void navigate(targetRoute || GENERAL_ROUTES.Index);
    },
  });
};

export const useFastOrgSwitch = (targetRoute?: string) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  return useMutation<LoginResponseBodyUserType, Error, any, any>({
    meta: { suppressToast: true },
    mutationKey: [{ scope: "user" }],
    mutationFn: async (body: {
      token: string;
      orgId?: string;
      shopId?: string;
    }) => switchOrganization(body.token, body.orgId, body.shopId),
    onSuccess: async (data) => {
      dispatch(clearGlobal());
      await shopifyGqlClient.resetStore();

      // eslint-disable-next-line react-compiler/react-compiler
      IgStorage.authToken = data.token;
      dispatch(clearDashboardFilters());
      dispatch(clearSitewideDashboardFilters());
      dispatch(resetExperience());
      dispatch(resetRedirects());
      dispatch(resetUserInterfaces());
      dispatch(resetWidgets());
      dispatch(clearSnoozeShopifyScopesDialog());
      dispatch(resetCartTargeting());
      dispatch(resetUpsellProductRules());
      queryClient.clear();
      queryClient.setQueryData(userKeys.all, data);
      queryClient.setQueryData(organizationsKeys.one, {
        org: data.organization,
      });
    },
  });
};
